import { CARRIER_DEPENDENT_STATUSES } from './constants';
import type { FormAction, FormState } from './types';

/**
 * A cents rate as the dollar string the fee input holds. Zero renders blank
 * rather than "0" — an unset rate and a free one look different on screen.
 */
const centsToDollarInput = (cents: number): string => (cents ? String(cents / 100) : '');

/**
 * The form as a saved load starts it.
 *
 * Every money field here is a **dollar string**, because that is what the
 * inputs show. The server columns behind them are all cents.
 */
export function buildInitialState(item: any): FormState {
  return {
    nextStatus: item.status ?? '',
    poNumber: item.order?.poNumber ?? '',
    customerId: item.order?.customerId ?? '',
    selectedProductId: item.productId ?? item.product?.id ?? '',
    // Use saved location; fall back to first available choice for pending items
    locationId:
      item.location?.id ?? item.reservedLocation?.id ?? item.choices?.locations?.[0]?.id ?? '',
    loadOptionId: item.orgLoadOptionIdSnapshot ?? '',
    palletSizeId: item.palletIdSnapshot ?? '',
    // Prisma serializes the Decimal quantity as a string ("23.54"); coerce to a
    // real number so `quantity: number` holds and downstream numeric checks work.
    quantity: Number(item.quantity) || 0,
    ticketNumber: item.ticketNumber ?? '',
    priceOverrideInput:
      item.lineTotal && item.loadCountSnapshot
        ? String(Math.round(item.lineTotal / item.loadCountSnapshot))
        : '',
    // deliveryFeeSnapshot is CENTS on the server; the input shows dollars.
    deliveryFeeInput:
      item.deliveryFeeSnapshot != null && item.deliveryFeeSnapshot > 0
        ? String(item.deliveryFeeSnapshot / 100)
        : '',
    deliveryFeeTouched: item.deliveryFeeSnapshot != null && item.deliveryFeeSnapshot > 0,
    // carrierRate is stored in CENTS on the server
    carrierRateInput:
      item.carrierRate != null && item.carrierRate > 0 ? String(item.carrierRate / 100) : '',
    pickupDate: item.pickupDate ? new Date(item.pickupDate).toISOString().slice(0, 10) : '',
    deliveryDate: item.deliveryDate ? new Date(item.deliveryDate).toISOString().slice(0, 10) : '',
    pickupNote: item.pickupNote ?? '',
    deliveryNote: item.deliveryNote ?? '',
    internalNotes: item.internalNotes ?? '',
    deliveryAddressId: item.order?.deliveryAddress?.id ?? '',
    driverName: item.driverName ?? '',
    truckNumber: item.truckNumber ?? '',
    localCarrier: item.carrier ?? null,
    driverContactId: item.driverContactId ?? undefined,
    pendingCarrierId: undefined,
    tags: Array.isArray(item.order?.tags) ? item.order.tags : [],
  };
}

export function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SET_STATUS':
      return { ...state, nextStatus: action.value };
    case 'SET_PO_NUMBER':
      return { ...state, poNumber: action.value };
    // Switching the customer reassigns the order. Clear everything that's
    // customer/location-specific — the Ship To and method/fee that cascade from
    // it, and the product (it may not be enabled at the new customer's location,
    // so the admin re-picks it rather than seeing a stale/invalid selection).
    case 'SET_CUSTOMER':
      return {
        ...state,
        customerId: action.customerId,
        deliveryAddressId: '',
        loadOptionId: '',
        deliveryFeeInput: '',
        deliveryFeeTouched: false,
        selectedProductId: '',
      };
    case 'SET_PRODUCT':
      return { ...state, selectedProductId: action.value };
    /*
     * The action carries the address x warehouse carrier rate in cents; the
     * field is dollars. This used to store the cents value verbatim, which is
     * what made any save after a warehouse change send the fee 100x too high.
     *
     * It still overwrites a fee the user already typed — `deliveryFeeTouched`
     * is recorded but not consulted. That is a separate question (which should
     * win, the typed fee or the new warehouse's rate?) and is left as-is.
     */
    case 'SET_LOCATION':
      return {
        ...state,
        locationId: action.locationId,
        deliveryFeeInput: centsToDollarInput(action.carrierRateCents),
      };
    case 'SET_LOAD_OPTION':
      return action.carrierRateCents !== undefined
        ? {
            ...state,
            loadOptionId: action.value,
            deliveryFeeInput: centsToDollarInput(action.carrierRateCents),
          }
        : { ...state, loadOptionId: action.value };
    case 'SET_PALLET_SIZE':
      return { ...state, palletSizeId: action.value };
    case 'SET_QUANTITY':
      return { ...state, quantity: action.value };
    case 'SET_TICKET_NUMBER':
      return { ...state, ticketNumber: action.value };
    case 'SET_PRICE_OVERRIDE':
      return { ...state, priceOverrideInput: action.value };
    case 'SET_DELIVERY_FEE':
      return { ...state, deliveryFeeInput: action.value, deliveryFeeTouched: true };
    case 'SET_CARRIER_RATE':
      return { ...state, carrierRateInput: action.value };
    case 'SET_PICKUP_DATE':
      return { ...state, pickupDate: action.value };
    case 'SET_DELIVERY_DATE':
      return { ...state, deliveryDate: action.value };
    case 'SET_PICKUP_NOTE':
      return { ...state, pickupNote: action.value };
    case 'SET_DELIVERY_NOTE':
      return { ...state, deliveryNote: action.value };
    case 'SET_INTERNAL_NOTES':
      return { ...state, internalNotes: action.value };
    // Same conversion as SET_LOCATION above.
    case 'SET_DELIVERY_ADDRESS':
      return {
        ...state,
        deliveryAddressId: action.addressId,
        deliveryFeeInput: centsToDollarInput(action.carrierRateCents),
        loadOptionId: action.loadOptionId !== undefined ? action.loadOptionId : state.loadOptionId,
      };
    case 'SET_DRIVER':
      return { ...state, driverName: action.value };
    case 'SET_DRIVER_CONTACT':
      return { ...state, driverContactId: action.contactId, driverName: action.driverName };
    case 'SET_TRUCK':
      return { ...state, truckNumber: action.value };
    case 'ASSIGN_CARRIER':
      return {
        ...state,
        localCarrier: action.carrier,
        pendingCarrierId: action.carrier.id,
        driverName: '',
        truckNumber: '',
        driverContactId: null,
        // A delivered or cancelled load keeps its status — see
        // `assigningCarrierAdvancesStatus`.
        nextStatus: action.advanceToAssigned ? 'ASSIGNED' : state.nextStatus,
      };
    case 'REMOVE_CARRIER':
      return {
        ...state,
        localCarrier: null,
        pendingCarrierId: null,
        // The driver and truck belonged to the carrier being removed.
        driverName: '',
        truckNumber: '',
        driverContactId: null,
        nextStatus: CARRIER_DEPENDENT_STATUSES.includes(action.prevStatus)
          ? 'APPROVED'
          : action.prevStatus,
      };
    case 'RESET':
      return action.state;
    case 'SET_TAGS':
      return {
        ...state,
        tags: action.value,
      };
    default:
      return state;
  }
}

/** The reset action `useEditSession` dispatches when it adopts a new baseline. */
export const RESET_FORM = (state: FormState): FormAction => ({ type: 'RESET', state });

/**
 * Fields that are transient UI state rather than form values, so they never make
 * the dialog dirty on their own.
 *
 * `localCarrier` mirrors the carrier card on screen and `deliveryFeeTouched`
 * records that someone typed in the fee box — both change only alongside a field
 * that is compared, so neither adds information. `pendingCarrierId` is the
 * carrier the next save will apply; it is handled explicitly by the dirty check,
 * because "a carrier is staged" is genuinely unsaved work even though nothing
 * else in the form may have changed.
 */
export const UNTRACKED_FORM_FIELDS: (keyof FormState)[] = [
  'localCarrier',
  'deliveryFeeTouched',
  'pendingCarrierId',
];

/**
 * Human labels for the fields the dirty check tracks, so a confirm dialog can
 * name what is about to be discarded instead of just asserting that something
 * is. Anything without a label falls back to its key.
 */
export const FORM_FIELD_LABELS: Partial<Record<keyof FormState, string>> = {
  nextStatus: 'Status',
  poNumber: 'PO number',
  customerId: 'Customer',
  selectedProductId: 'Product',
  locationId: 'Warehouse',
  loadOptionId: 'Fulfilment method',
  palletSizeId: 'Pallet size',
  quantity: 'Quantity',
  ticketNumber: 'Ticket number',
  priceOverrideInput: 'Price override',
  deliveryFeeInput: 'Carrier rate',
  carrierRateInput: 'Direct ship fee',
  pickupDate: 'Pickup date',
  deliveryDate: 'Delivery date',
  pickupNote: 'Pickup note',
  deliveryNote: 'Delivery note',
  internalNotes: 'Internal notes',
  deliveryAddressId: 'Ship To',
  driverName: 'Driver',
  truckNumber: 'Truck #',
  driverContactId: 'Driver contact',
  tags: 'Tags',
};
